import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"

import LoadingSpinner from "../../../components/shared/LoadingSpinner"

const CalendarIcon = ({ theme }) => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="4"
      width="18"
      height="18"
      rx="2"
      ry="2"
      stroke={theme?.colors?.textDark || "rgba(85, 67, 63, 1)"}
      strokeWidth="2"
      fill="none"
    />
    <line x1="16" y1="2" x2="16" y2="6" stroke={theme?.colors?.textDark || "rgba(85, 67, 63, 1)"} strokeWidth="2" />
    <line x1="8" y1="2" x2="8" y2="6" stroke={theme?.colors?.textDark || "rgba(85, 67, 63, 1)"} strokeWidth="2" />
    <line x1="3" y1="10" x2="21" y2="10" stroke={theme?.colors?.textDark || "rgba(85, 67, 63, 1)"} strokeWidth="2" />
  </svg>
)

const ServiceAvailableTimes = ({
  availableTimes,
  selectedLocation,
  loading,
  error,
  timeZone,
  onTimeSelect,
  selectedTime,
  theme
}) => {
  const [displayedDays, setDisplayedDays] = useState(2)

  // Show only the first 5 days initially
  const visibleDays = availableTimes.slice(0, displayedDays)

  // Reset displayed days when available times change
  useEffect(() => {
    setDisplayedDays(5)
  }, [availableTimes.length])

  const formatDate = (dateString) => {
    const momentDate = dayjs(dateString)
    const dateFormat = "dddd, MMMM Do"
    const isToday = dayjs().format(dateFormat) === momentDate.format(dateFormat)
    const isTomorrow = dayjs().add(1, "days").format(dateFormat) === momentDate.format(dateFormat)

    if (isToday) {
      return `Today, ${momentDate.format("MMMM D")}`
    } else if (isTomorrow) {
      return `Tomorrow, ${momentDate.format("MMMM D")}`
    } else {
      return momentDate.format(dateFormat)
    }
  }

  const formatTime = (timeString) => {
    // Convert time string to a readable format like "8:30am", "9:30am"
    if (!timeString) return "Invalid Time"

    try {
      // Handle different time formats that might be passed
      let time

      // If it's already in HH:mm format (e.g., "08:30", "14:30")
      if (/^\d{1,2}:\d{2}$/.test(timeString)) {
        time = dayjs(`2000-01-01T${timeString}`)
      }
      // If it's in HH:mm:ss format (e.g., "08:30:00")
      else if (/^\d{1,2}:\d{2}:\d{2}$/.test(timeString)) {
        time = dayjs(`2000-01-01T${timeString}`)
      }
      // If it's already a full ISO string
      else if (timeString.includes("T") || timeString.includes("Z")) {
        time = dayjs(timeString)
      }
      // If it's just a number (minutes since midnight)
      else if (/^\d+$/.test(timeString)) {
        const hours = Math.floor(parseInt(timeString) / 60)
        const minutes = parseInt(timeString) % 60
        const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
        time = dayjs(`2000-01-01T${formattedTime}`)
      }
      // Try parsing as is
      else {
        time = dayjs(`2000-01-01T${timeString}`)
      }

      // Check if the parsed time is valid
      if (!time.isValid()) {
        console.warn(`Invalid time format: ${timeString}`) // eslint-disable-line no-console
        return timeString // Return original if parsing fails
      }

      return time.format("h:mma")
    } catch (error) {
      console.error(`Error formatting time "${timeString}":`, error) // eslint-disable-line no-console
      return timeString // Return original if parsing fails
    }
  }

  const getTimeZoneAbbr = () => {
    if (selectedLocation?.kind === "virtual") {
      return new Date().toLocaleTimeString("en-us", { timeZoneName: "short" }).split(" ")[2]
    }
    return selectedLocation?.timeZoneAbbr || timeZone || ""
  }

  const handleTimeClick = (date, time) => {
    const selection = { date, time }
    if (onTimeSelect) {
      onTimeSelect(selection)
    }
  }

  const isTimeSelected = (date, time) => selectedTime && selectedTime.date === date && selectedTime.time === time

  const handleLoadMoreDays = () => {
    setDisplayedDays((prev) => prev + 5)
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex w-full flex-col items-start justify-start gap-8">
        <h2
          className="text-[20px] font-semibold leading-[32px]"
          style={{
            fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
            color: theme?.colors?.textDark || "#55433f"
          }}>
          Available Times
        </h2>
        <div className="flex w-full justify-center py-16">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex w-full flex-col items-start justify-start gap-8">
        <h2
          className="text-[20px] font-semibold leading-[32px]"
          style={{
            fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
            color: theme?.colors?.textDark || "#55433f"
          }}>
          Available Times
        </h2>
        <div className="flex w-full flex-col items-center gap-6 py-16 text-center">
          <div className="text-6xl">⚠️</div>
          <h3
            className="text-[27px] font-bold leading-[40px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              color: theme?.colors?.textDark || "#55433f"
            }}>
            Unable to load availability
          </h3>
          <p
            className="text-[18px] font-normal leading-[32px]"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              color: theme?.colors?.textDark || "#55433f"
            }}>
            Please try again later or contact us for assistance.
          </p>
        </div>
      </div>
    )
  }

  // No available times
  if (!availableTimes || availableTimes.length === 0) {
    return (
      <div className="flex w-full flex-col items-start justify-start gap-8">
        <h2
          className="text-[20px] font-semibold leading-[32px]"
          style={{
            fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
            color: theme?.colors?.textDark || "#55433f"
          }}>
          Available Times
        </h2>
        <div className="flex w-full flex-col items-center gap-6 py-16 text-center">
          <div className="text-6xl">📅</div>
          <h3
            className="text-[27px] font-bold leading-[40px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              color: theme?.colors?.textDark || "#55433f"
            }}>
            No available times
          </h3>
          <p
            className="text-[18px] font-normal leading-[32px]"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              color: theme?.colors?.textDark || "#55433f"
            }}>
            Please check back later or contact us to schedule an appointment.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-start justify-start gap-8">
      <h2
        className="text-[20px] font-semibold leading-[32px]"
        style={{
          fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
          color: theme?.colors?.textDark || "#55433f"
        }}>
        Available Times
      </h2>

      <div className="flex w-full flex-col gap-6">
        {visibleDays.map((day) => (
          <div key={day.date} className="flex w-full flex-col gap-4">
            {/* Date Header */}
            <div className="flex items-center gap-3">
              <CalendarIcon theme={theme} />
              <h3
                className="text-[18px] font-semibold leading-[28px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.textDark || "#55433f"
                }}>
                {formatDate(day.date)}
              </h3>
              {getTimeZoneAbbr() && (
                <span
                  className="text-[14px] font-normal leading-[20px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    color: theme?.colors?.textLight || "#a4786e"
                  }}>
                  ({getTimeZoneAbbr()})
                </span>
              )}
            </div>

            {/* Time Slots */}
            <div className="grid grid-cols-6 gap-2 lg:grid-cols-6 md:grid-cols-6 sm:grid-cols-4">
              {day.times.map((time) => (
                <button
                  key={`${day.date}-${time}`}
                  onClick={() => handleTimeClick(day.date, time)}
                  className={`px-3 py-2 text-center transition-colors ${
                    isTimeSelected(day.date, time) ? "font-semibold" : "hover:bg-gray-50"
                  }`}
                  style={{
                    backgroundColor: isTimeSelected(day.date, time)
                      ? theme?.colors?.primary || "#bd654f"
                      : theme?.colors?.background || "#ffffff",
                    color: isTimeSelected(day.date, time)
                      ? theme?.colors?.textLight || "#FFFFFF"
                      : theme?.colors?.textDark || "#55433f",
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    border: `1px solid ${
                      isTimeSelected(day.date, time)
                        ? theme?.colors?.primary || "#bd654f"
                        : theme?.colors?.border || "#e6e6e6"
                    }`,
                    fontSize: "14px",
                    fontWeight: isTimeSelected(day.date, time) ? "600" : "500",
                    lineHeight: "20px",
                    borderRadius: theme?.buttonShape?.borderRadius || "32px"
                  }}>
                  {formatTime(time)}
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Load More Link */}
        <div className="flex flex-col gap-4">
          {/* Load more days if we have more days to show */}
          {visibleDays.length < availableTimes.length && (
            <button
              onClick={handleLoadMoreDays}
              className="text-left underline decoration-solid transition-colors hover:no-underline"
              style={{
                color: theme?.colors?.primary || "#bd654f",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontSize: "16px",
                fontWeight: "500",
                borderRadius: theme?.buttonShape?.borderRadius || "0.375rem"
              }}>
              Load more times
            </button>
          )}
        </div>
      </div>

      {/* Divider */}
      <hr
        className="w-full border-t"
        style={{
          borderColor: theme?.colors?.border || "#e6e6e6"
        }}
      />
    </div>
  )
}

ServiceAvailableTimes.propTypes = {
  availableTimes: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      times: PropTypes.arrayOf(PropTypes.string).isRequired
    })
  ).isRequired,
  selectedLocation: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object,
  timeZone: PropTypes.string,
  onTimeSelect: PropTypes.func,
  onLoadMore: PropTypes.func,
  hasMoreData: PropTypes.bool,
  loadingMore: PropTypes.bool,
  selectedTime: PropTypes.object,
  theme: PropTypes.object
}

export default ServiceAvailableTimes
